import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { CalendarCheck, PawPrint, Users, Bell, Settings as Cog, Siren, CalendarClock, ShoppingCart } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { api, fmtAgo } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { currentSubscription } from "@/lib/push";

const NAV = [
  { to: "/app", label: "Today", icon: CalendarCheck, end: true },
  { to: "/app/pets", label: "Pets", icon: PawPrint },
  { to: "/app/reminders", label: "Due", icon: CalendarClock },
  { to: "/app/share", label: "Share", icon: Users },
  { to: "/app/settings", label: "Settings", icon: Cog },
];

export const HouseholdCtx = React.createContext(null);

function useNotifications() {
  const [items, setItems] = useState([]);
  const seen = useRef(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
      const fresh = data.filter((n) => !n.read && seen.current && !seen.current.has(n.id));
      if (fresh.length && "Notification" in window && Notification.permission === "granted" && !(await currentSubscription().catch(() => null))) {
        const reg = await navigator.serviceWorker?.getRegistration();
        fresh.slice(0, 3).forEach((n) => {
          const opts = { body: n.body, icon: "/icon-192.png", tag: n.id, data: { url: "/app" } };
          reg ? reg.showNotification(n.title, opts) : new Notification(n.title, opts);
        });
      }
      seen.current = new Set(data.map((n) => n.id));
    } catch (e) { /* offline */ }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);
  return { items, load };
}

function NotificationBell() {
  const { items, load } = useNotifications();
  const unread = items.filter((n) => !n.read).length;
  const markRead = async (open) => {
    if (open && unread) {
      await api.post("/notifications/read").catch(() => {});
      load();
    }
  };
  return (
    <Sheet onOpenChange={markRead}>
      <SheetTrigger asChild>
        <button data-testid="notifications-bell-btn" className="relative h-11 w-11 grid place-items-center rounded-full bg-white border border-[#EFECE6]">
          <Bell className="h-5 w-5" />
          {unread > 0 && <span data-testid="notifications-unread-count" className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-[#E07A5F] text-white text-[11px] font-bold grid place-items-center">{unread}</span>}
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[92vw] sm:max-w-md bg-[#FAF7F2]">
        <SheetHeader><SheetTitle className="font-heading">Notifications</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-2 overflow-y-auto max-h-[80vh]" data-testid="notifications-list">
          {items.length === 0 && <p className="text-sm text-slate-500">Nothing yet. Reminders and sitter updates show up here.</p>}
          {items.map((n) => (
            <div key={n.id} className={`pl-card p-3 ${n.kind === "missed" ? "border-[#E07A5F]" : ""}`}>
              <p className="font-semibold text-sm">{n.title}</p>
              <p className="text-xs text-slate-500">{n.body} · {fmtAgo(n.created_at)}</p>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function TrialBanner({ access }) {
  if (!access || access.subscription_status === "active" || access.subscription_status === "trialing") return null;
  const expired = !access.has_access;
  return (
    <Link to="/app/settings#billing" data-testid="trial-banner" className={`block text-center text-sm font-semibold py-2 px-4 ${expired ? "bg-[#E07A5F] text-white" : "bg-[#FFF7E6] text-[#92400E]"}`}>
      {expired ? "Your free trial has ended. Subscribe to keep logging →" : `${access.trial_days_left} days left in your free trial · Subscribe`}
    </Link>
  );
}

export default function AppShell({ children }) {
  const [household, setHousehold] = useState(null);
  const nav = useNavigate();
  const reload = useCallback(() => api.get("/household").then((r) => setHousehold(r.data)).catch(() => {}), []);
  useEffect(() => { reload(); }, [reload]);
  return (
    <HouseholdCtx.Provider value={{ household, reload }}>
      <div className="min-h-screen bg-[#FAF7F2] pb-24">
        <TrialBanner access={household?.access} />
        <header className="no-print sticky top-0 z-30 bg-[#FAF7F2]/85 backdrop-blur-xl border-b border-[#EFECE6]">
          <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
            <Link to="/app" data-testid="app-logo-link"><Logo /></Link>
            <div className="flex items-center gap-2">
              <NavLink to="/app/shopping" data-testid="shopping-quick-btn" aria-label="Shopping list" className={({ isActive }) => `h-11 w-11 grid place-items-center rounded-full border ${isActive ? "bg-[#F59E0B] border-[#F59E0B]" : "bg-white border-[#EFECE6]"}`}><ShoppingCart className="h-5 w-5" /></NavLink>
              <button data-testid="emergency-quick-btn" onClick={() => nav("/app/emergency")} className="h-11 px-3 rounded-full bg-[#FDF2F0] text-[#C2410C] border border-[#F6D5CC] flex items-center gap-1.5 text-sm font-bold">
                <Siren className="h-4 w-4" /> SOS
              </button>
              <NotificationBell />
            </div>
          </div>
        </header>
        <main className="max-w-3xl mx-auto px-4 pt-5">{children}</main>
        <nav className="no-print fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t border-[#EFECE6] pb-safe">
          <div className="max-w-3xl mx-auto grid grid-cols-5">
            {NAV.map(({ to, label, icon: Icon, end }) => (
              <NavLink key={to} to={to} end={end} data-testid={`nav-${label.toLowerCase()}`}
                className={({ isActive }) => `flex flex-col items-center gap-0.5 pt-2.5 pb-1 text-[11px] font-semibold ${isActive ? "text-[#D97706]" : "text-slate-500"}`}>
                <Icon className="h-6 w-6" />{label}
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
    </HouseholdCtx.Provider>
  );
}
